import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { usePricingScenarios } from '../../../../graphql/queries/settlement/getSettlementCommonQueries';
import { OPS_TOOL_SETTLEMENT_CLIENTS_BY_SCENARIO } from '../graphql/opsToolSettlementGraphql';

dayjs.extend(utc);

export const ALL_CLIENTS_VALUE = '__ALL__';

export const INCOME_DELIVERY_SCENARIO = 1001001;
export const DSP_DELIVERY_SCENARIO = 1001002;

export const BILL_TYPE_OPTIONS_BY_SCENARIO: Record<number, { value: number; label: string }[]> = {
  [INCOME_DELIVERY_SCENARIO]: [
    { value: 0, label: '运费账单' },
    { value: 1, label: 'SLA账单' },
  ],
  [DSP_DELIVERY_SCENARIO]: [
    { value: 2, label: 'DSP运费账单' },
  ],
};

function defaultBillTypeForScenario(scenario: number | undefined): number | undefined {
  if (scenario == null) return undefined;
  return BILL_TYPE_OPTIONS_BY_SCENARIO[scenario]?.[0]?.value;
}

export function useOpsToolScenarioFilters() {
  const { scenarios, loading: scenariosLoading } = usePricingScenarios();
  const [businessScenarioCode, setBusinessScenarioCode] = useState<number | undefined>();
  const [billType, setBillType] = useState<number | undefined>();
  const [settlementClientId, setSettlementClientId] = useState<string>(ALL_CLIENTS_VALUE);
  const [periodRange, setPeriodRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const scenarioOptions = useMemo(
    () => scenarios.map(s => ({ value: Number(s.businessScenarioCode), label: s.businessScenarioName })),
    [scenarios]
  );

  const billTypeOptions = useMemo(
    () => (businessScenarioCode == null ? [] : BILL_TYPE_OPTIONS_BY_SCENARIO[businessScenarioCode] ?? []),
    [businessScenarioCode]
  );

  const [loadClients, { data: clientsData, loading: clientsLoading }] = useLazyQuery(
    OPS_TOOL_SETTLEMENT_CLIENTS_BY_SCENARIO
  );

  const clientOptions = useMemo(() => {
    const options = (clientsData?.opsToolSettlementClientsByScenario ?? []).map(
      (c: { value: string; label: string }) => ({ value: c.value, label: c.label })
    );
    return [{ value: ALL_CLIENTS_VALUE, label: '全部客户' }, ...options];
  }, [clientsData]);

  useEffect(() => {
    if (scenarioOptions.length > 0 && businessScenarioCode == null) {
      setBusinessScenarioCode(scenarioOptions[0].value);
    }
  }, [scenarioOptions, businessScenarioCode]);

  useEffect(() => {
    if (businessScenarioCode == null) return;
    setSettlementClientId(ALL_CLIENTS_VALUE);
    setBillType(defaultBillTypeForScenario(businessScenarioCode));
    loadClients({ variables: { businessScenarioCode } });
  }, [businessScenarioCode, loadClients]);

  const buildFilters = () => {
    const periodStartFrom = periodRange?.[0]
      ? dayjs(periodRange[0]).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
      : undefined;
    const periodEndTo = periodRange?.[1]
      ? dayjs(periodRange[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
      : undefined;
    return {
      businessScenarioCode: businessScenarioCode!,
      billType: billType!,
      settlementClientId:
        settlementClientId === ALL_CLIENTS_VALUE ? null : Number(settlementClientId),
      periodStartFrom,
      periodEndTo,
    };
  };

  return {
    businessScenarioCode,
    setBusinessScenarioCode,
    billType,
    setBillType,
    billTypeOptions,
    settlementClientId,
    setSettlementClientId,
    periodRange,
    setPeriodRange,
    scenarioOptions,
    scenariosLoading,
    clientOptions,
    clientsLoading,
    buildFilters,
  };
}
